import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { State, Action, Selector, StateContext, Store } from '@ngxs/store';
import { CreatedSalesShippingInstructionWebhook, DeleteSalesShippingInstructionComponent, ExportSalesShippingInstructions, GetAvailableSalesConfirmations, GetSalesShippingComponentsByParentId, GetSalesShippingInstructions, GetSalesShippingInstructionsDocumentTemplate, GetSalesShippingInstructionsSpecialInstructions, GetSalesShippingInstructionsStatuses, GetSalesShippingInstructionsTerms, GetStockAvailable, OnActionSalesShippingInstruction, OnActionSalesShippingStock, OnDeleteSalesShippingInstruction, OnGetDataForEditOrClone, OnSetCloneMode, OnSetCurrentSSIRenderer, OnSetSalesShippingInstructionDates, OnSetSalesShippingInstructionStatus, RemoveSalesShippingProduct, ResetDocumentationSpecialInstructions, ResetSelectedRequiredScStock, SetSalesConfirmationSelected, SetSalesShippingComponentsGridConfig, SetSalesShippingStockEditable, SetSalesShippingStockIdentification, SetSelectedPsiGradeStock, SetSelectedRequiredScStock, SetSelectedRowNodeForEdit, SetSelectedSalesShippingInstruction, SetShippingInstructionStockGridConfig, UpdatedSalesShippingInstructionWebhook } from './sales-shipping-instructions.actions';
import { AvailableSalesConfirmations, AvailableScGrade, SalesShippingInstructionsStateModel, StockAvailableGrade } from './sales-shipping-instructions.model';
import { SalesShippingInstructionsService } from '../sales-shipping-instructions.service';
import { catchError, firstValueFrom, tap } from 'rxjs';
import { OnCanCloseDialog, OnSetSharedDropdowns, OnShowFuseAlert, ResetStatusForm, SetShowLoader, ShowToastrAlert } from 'app/modules/shared-state/shared.actions';
import { OnSetSharedGridConfig, OnSetSharedGridRowData, SetCanRowDrag } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions';
import { SharedGridState } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.state';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { produce } from 'immer';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { SharedState } from 'app/modules/shared-state/shared.state';
import { resolveExportHttpErrorMessage } from 'app/modules/shared/http-error-message.util';

@State<SalesShippingInstructionsStateModel>({
  name: 'salesShippingInstructions',
  defaults: {
    items: [],

    selectedSalesShippingInstruction: { _id: null, md5Checksum: null },

    salesShippingInstructionsTerms: [],

    editingSSIDocuments: { documentRows: [], documentDescription: '' },
    editingSpecialInstructions: [],

    isCloneMode: false,

    selectedSalesConfirmation: {
      _id: null,
      salesConfirmationNumber: null,
    },

    salesShippingInstructionsForm: {
      model: {
        ssiDate: null,
        salesConfirmationReferences: null,
        status: null,
        customerReference: null,
        shipmentTo: null,
        shipmentCc: null,
        customerName: null,
        customerId: null,
        subject: null,
        supplierShipperDetails: null,
        consigneeDetails: null,
        notifyPartyDetails: null,
        additionalNotifyPartyDetails: null,
      },
      dirty: false,
      status: '',
      errors: {}
    },
    salesShippingStockForm: {
      model: {
        _id: null,
        parentId: null,
        shipmentReference: null,
        purchaseConfirmationId: null,
        scInternalGrade: null,
        stockInternalGrade: null,
        shippingMark: null,
        cropYear: null,
        storageFacility: null,
        numbering: null,
        quantityKgs: null,
        cartonNettWeight: null,
        noOfCartons: null,
        salesConfirmationComponentId: null,
        internalGradeId: null,
        newRow: false,
        updated: false,
      },
      dirty: false,
      status: '',
      errors: {}
    },
    salesShippingInstructions: {
      model: {
        methodOfShipment: null,
        shippingLine: null,
        timing: null,
        incotermType: null,
        paymentTerms: null,
        portOfLoading: null,
        portOfDestination: null,
        descriptionOfGoods: null,
        fumigation: null,
        photographs: null,
        sampleRequirements: null,
        reporting: null,
      },
      dirty: false,
      status: '',
      errors: {}
    },
    salesShippingDocumentationNotesForm: {
      model: {
        documentationNotes: null,
      },
      dirty: false,
      status: '',
    },
    isSalesShippingStockEditable: false,
    availableSalesConfirmations: [],

    originalAvailableScGrades: [],
    availableScGrades: [],

    groupedStockAvailable: [],

    selectedRequiredScStock: {
      salesConfirmationComponentId: null,
      internalGrade: null,
      externalGrade: null,
      quantityKgs: null,
      cartonNettWeight: null,
      requiredCartons: null,
    },

    selectedPsiGradeStock: {
      _id: null,
      internalGrade: null,
      externalGrade: null,
      quantityKgs: null,
      availableCartons: null,
      cartonNettWeight: null
    },

    selectedRowNodeForEdit: null,
    salesShippingStockIdentification: null,

    currentRendererId: null,
    editedRowIndex: null,

    datesForm: {
      model: {
        estimatedTimeOfDeparture: null,
        estimatedTimeOfArrival: null,
        billOfLadingDate: null,
      },
      dirty: false,
      status: '',
    },
  }
})
@Injectable()
export class SalesShippingInstructionsState {

  private _store = inject(Store);
  private _salesShippingInstructionsService = inject(SalesShippingInstructionsService);
  private _router = inject(Router);

  @Action(ResetDocumentationSpecialInstructions)
  resetDocumentationSpecialInstructions(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.patchState({
      editingSSIDocuments: { documentRows: [], documentDescription: '' },
      editingSpecialInstructions: [],
    });
  }

  @Action(GetSalesShippingInstructionsTerms)
  getSalesShippingInstructionsTerms(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    return this._salesShippingInstructionsService.getSalesShippingInstructionsTerms().pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: any) => {

        const salesShippingInstructionsObj: any = {};

        for (const item of data) {
          salesShippingInstructionsObj[item.key] = item.value;
        }

        ctx.patchState({ salesShippingInstructionsTerms: data });
        ctx.dispatch(new UpdateFormValue({
          path: 'salesShippingInstructions.salesShippingInstructions',
          value: {
            methodOfShipment: salesShippingInstructionsObj.methodOfShipment,
            shippingLine: null,
            timing: null,
            incotermType: null,
            paymentTerms: null,
            portOfDestination: null,
            portOfLoading: salesShippingInstructionsObj.portOfLoading,
            descriptionOfGoods: salesShippingInstructionsObj.descriptionOfGoods,
            fumigation: salesShippingInstructionsObj.fumigation,
            photographs: salesShippingInstructionsObj.photographs,
            sampleRequirements: salesShippingInstructionsObj.sampleRequirements,
            reporting: salesShippingInstructionsObj.reporting,
          }
        }));
      })
    );
  }

  @Selector()
  static getSalesShippingInstructionsTerms(state: SalesShippingInstructionsStateModel) {
    return state.salesShippingInstructionsTerms;
  }

  //** Get the list of sales shipping instructions */
  @Action(GetSalesShippingInstructions)
  getSalesShippingInstructions(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    return this._salesShippingInstructionsService.getSalesShippingInstructions().pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;

      }),
      tap((data: any) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: data,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
        }));
        ctx.dispatch(new SetShowLoader(false));
      })
    );
  }

  @Action(ExportSalesShippingInstructions)
  exportSalesShippingInstructions(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    return this._salesShippingInstructionsService.exportSalesShippingInstructions().pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: resolveExportHttpErrorMessage(error, 'Could not export sales shipping instructions.'),
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) {
          ctx.dispatch(new SetShowLoader(false));
          return;
        }
        const header = response.headers.get('Content-Disposition');
        let filename = `sales-shipping-instructions-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
        if (header) {
          const match = /filename="([^"]+)"/.exec(header) ?? /filename=([^;\s]+)/.exec(header);
          if (match) {
            filename = match[1];
          }
        }
        saveAs(blob, filename);
        ctx.dispatch(new SetShowLoader(false));
      })
    );
  }

  //** Get the list of available sales confirmations */
  @Action(GetAvailableSalesConfirmations)
  getAvailableSalesConfirmations(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    return this._salesShippingInstructionsService.getAvailableSalesConfirmations().pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: AvailableSalesConfirmations[]) => {
        let availableScGrades = [];

        const filteredData = data
          .filter(item => item !== null)
          .filter(item => {
            // Check if the item has at least one non-depleted grade
            return item.availableScGrades.some(grade => !grade.depleted);
          });

        if (!ctx.getState().selectedSalesShippingInstruction.salesConfirmationId) {

          if (ctx.getState().isCloneMode) {
            const rowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID].rowData;

            const foundRow = rowData.find((row: any) => row._id === ctx.getState().selectedSalesShippingInstruction._id);

            const flattenedAvailableScGrades = data.map(item => item.availableScGrades).flat();

            const filteredForEditScGrades = flattenedAvailableScGrades.filter(item => item.salesConfirmationId === foundRow.salesConfirmationId);

            availableScGrades = filteredForEditScGrades;
          } else {
            availableScGrades = filteredData.map(item => item.availableScGrades).flat();
          }

          ctx.patchState({
            availableSalesConfirmations: filteredData,
            originalAvailableScGrades: filteredData.map(item => item.availableScGrades).flat(),
            availableScGrades: availableScGrades,
          });
        } else {

          availableScGrades = filteredData.map(item => item.availableScGrades).flat();

          const filteredForEditScGrades = availableScGrades.filter(item => item.salesConfirmationId === ctx.getState().selectedSalesShippingInstruction.salesConfirmationId);

          ctx.patchState({
            availableSalesConfirmations: filteredData,
            originalAvailableScGrades: filteredData.map(item => item.availableScGrades).flat(),
            availableScGrades: filteredForEditScGrades,
          });

        }
      })
    );
  }

  @Selector()
  static getAvailableSalesConfirmations(state: SalesShippingInstructionsStateModel) {
    return state.availableSalesConfirmations;
  }

  @Selector()
  static getAvailableScGrades(state: SalesShippingInstructionsStateModel) {
    return state.availableScGrades;
  }


  //** Called when the form opens to get all the available stock from the PSI components which can be used for the selected sales confirmation. */
  @Action(GetStockAvailable)
  getStockAvailable(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    return this._salesShippingInstructionsService.getStockAvailable().pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnSetCloneMode(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: any) => {
        if (data.length <= 0) {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'No stock available',
            messageBody: 'All PSI stock has been fulfilled in other sales shipping instructions.',
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new OnSetCloneMode(false));
          throw new Error('No stock available');
        }
        ctx.patchState({ groupedStockAvailable: data });
      })
    );
  }

  //** Set the shipping instruction stock grid config in the form */
  @Action(SetShippingInstructionStockGridConfig)
  setShippingInstructionStockGridConfig(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetShippingInstructionStockGridConfig) {

    const store = inject(Store);
    const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

    const updatedGridConfig = produce(parentGridConfig, (draft) => {

      draft[payload.gridUUID] = payload.gridConfig[payload.gridUUID];
    });
    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
  }

  @Selector()
  static getStockAvailable(state: SalesShippingInstructionsStateModel) {
    return state.groupedStockAvailable;
  }

  //** Set the sales confirmation selected and alters the available sc grades based on the selected sales confirmation.
  //** This action is triggered when the user selects a sales confirmation from the dropdown and the available sc grades are updated based on the selected sales confirmation. 
  //* The data is fetched from the backend and the form is updated with the new data. */
  @Action(SetSalesConfirmationSelected)
  setSalesConfirmationSelected(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSalesConfirmationSelected) {
    const state = ctx.getState();

    ctx.dispatch(new OnSetSharedGridRowData({
      reload: true,
      payload: [],
      gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
    }));
    ctx.dispatch(new ResetSelectedRequiredScStock());

    const newState = produce(state, draft => {
      draft.availableScGrades = state.originalAvailableScGrades.filter(item => {
        return payload.availableScGrades.some(grade => grade.salesConfirmationComponentId === item.salesConfirmationComponentId);
      }
      );
    });

    ctx.patchState({ availableScGrades: newState.availableScGrades, selectedSalesConfirmation: payload });

    return this._salesShippingInstructionsService.getSalesConfirmationById(payload._id).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: any) => {
        ctx.dispatch(new UpdateFormValue({
          path: 'salesShippingInstructions.salesShippingInstructionsForm',
          value: {
            customerName: data.customer.name,
            customerId: data.customer._id,
          }
        }));
        ctx.dispatch(new UpdateFormValue({
          path: 'salesShippingInstructions.salesShippingInstructions',
          value: {
            incotermType: data.incoterm,
            paymentTerms: data.paymentTerms,
          }
        }));
      })
    );
  }


  @Action(OnActionSalesShippingStock)
  async onActionSalesShippingStock(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new OnShowFuseAlert({ showAlert: false }));

    const state = ctx.getState();
    const formData = state.salesShippingStockForm.model;

    let canProceed = true;
    for (const key in formData) {
      if (!formData[key] && key != '_id') {
        this._store.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Please enter field - ${key.toUpperCase()}`,
          position: 'toast-top-center'
        }));
        canProceed = false;
      }
      if (key === 'noOfCartons') {
        const cartons = formData[key];
        if (!Number.isInteger(+cartons)) {
          this._store.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Invalid Input',
            messageBody: 'Number of Cartons must be an integer.',
            position: 'toast-top-center'
          }));
          canProceed = false;
        }
      }
    }

    if (!canProceed) return;

    const selectedRequiredScStock = ctx.getState().selectedRequiredScStock;

    if (formData.quantityKgs > selectedRequiredScStock.quantityKgs) {
      ctx.dispatch(new OnShowFuseAlert({
        showAlert: true,
        type: 'error',
        title: 'Insufficient Stock',
        message: `SC stock required (${selectedRequiredScStock.quantityKgs.toLocaleString()}) is less than the quantity of stock selected (${formData.quantityKgs.toLocaleString()}).`,
      }));
      return;
    }

    if (formData.cartonNettWeight !== selectedRequiredScStock.cartonNettWeight) {
      ctx.dispatch(new OnShowFuseAlert({
        showAlert: true,
        type: 'error',
        title: 'Invalid Carton Nett Weight',
        message: `The carton nett weight of the SC (${selectedRequiredScStock.cartonNettWeight.toLocaleString()}) does not match the carton nett weight of the selected PSI stock (${formData.cartonNettWeight.toLocaleString()}).`,
      }));
      return;
    }

    const isEditNodeMode = state.isSalesShippingStockEditable;

    if (isEditNodeMode) {
      await this.editNodeMode(ctx, formData);
    } else {
      await this.addNodeMode(ctx, formData);
    }
    this._store.dispatch(new SetSelectedRowNodeForEdit(null));
    ctx.dispatch(new SetSelectedPsiGradeStock({
      _id: null,
      internalGrade: null,
      externalGrade: null,
      quantityKgs: null,
      availableCartons: null,
      cartonNettWeight: null
    }));
  }

  async addNodeMode(ctx: StateContext<SalesShippingInstructionsStateModel>, formData: any) {

    const availableScGrades = ctx.getState().availableScGrades;
    const selectedRequiredScStock = ctx.getState().selectedRequiredScStock;

    const availableInternalGrades = ctx.getState().groupedStockAvailable;

    const updatedGroupedStockAvailable = produce(availableInternalGrades, draft => {

      for (const grade of draft) {
        if (formData.internalGradeId === grade._id) {

          if (grade.availableCartons - formData.noOfCartons === 0) {
            grade['depleted'] = true;
          }

          grade.availableCartons = grade.availableCartons - formData.noOfCartons;
        }
      }
    });

    ctx.patchState({ groupedStockAvailable: updatedGroupedStockAvailable });

    for (const grade of availableScGrades) {
      if (grade.salesConfirmationComponentId === selectedRequiredScStock.salesConfirmationComponentId) {
        if (grade.remainingCartons === 0) {
          ctx.dispatch(new OnShowFuseAlert({
            showAlert: true,
            type: 'error',
            title: 'Stock Depleted',
            message: `Stock for '${grade.internalGrade}' has been depleted.`,
          }));
          throw new Error(`Stock for '${grade.internalGrade}' has been depleted.`);
        }

        if (grade.remainingCartons - formData.noOfCartons < 0) {
          // Store the values we need in local variables before creating the dialog
          const remainingCartons = grade.remainingCartons;
          const neededCartons = formData.noOfCartons;
          const gradeId = grade.salesConfirmationComponentId;

          const _fuseConfirmationService = inject(FuseConfirmationService);
          const dialogRef = _fuseConfirmationService.open({
            title: 'Selected Stock Warning',
            message: `You have selected more stock than is required for the selected sales confirmation grade. <br>

            <strong>Needed Stock: ${neededCartons}</strong> <br>
            <strong>Remaining Stock: ${remainingCartons}</strong> <br>

            <strong>Click Confirm to proceed with the remaining cartons of: ${remainingCartons}</strong>`,
            icon: {
              show: true,
              color: 'primary'
            },
            actions: {
              confirm: {
                color: 'accent'
              },
            },
            dismissible: false
          });

          // Return a promise that resolves after the dialog is closed
          return new Promise<void>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
              if (result === 'confirmed') {
                // Create a copy of formData with adjusted values
                const adjustedFormData = { ...formData };
                adjustedFormData.noOfCartons = remainingCartons;
                adjustedFormData.quantityKgs = remainingCartons * formData.cartonNettWeight;

                // Update the state directly
                const updatedAvailableScGrades = produce(ctx.getState().availableScGrades, draft => {
                  for (const grade of draft) {
                    if (grade.salesConfirmationComponentId === gradeId) {
                      grade.remainingCartons = 0;
                      grade['depleted'] = true;
                    }
                  }
                });

                // Update state and dispatch actions
                ctx.patchState({ availableScGrades: updatedAvailableScGrades });
                ctx.dispatch(new OnSetSharedGridRowData({
                  payload: [adjustedFormData],
                  gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
                }));
                ctx.dispatch(new ResetSelectedRequiredScStock());
              }
              resolve();
            });
          });
        }
      }
    }

    // If we reach here, no early returns were triggered
    const updatedAvailableScGrades = produce(availableScGrades, draft => {
      for (const grade of draft) {
        if (grade.salesConfirmationComponentId === selectedRequiredScStock.salesConfirmationComponentId) {
          if (grade.remainingCartons - formData.noOfCartons === 0) {
            grade['depleted'] = true;
          }
          grade.remainingCartons = grade.remainingCartons - formData.noOfCartons;
        }
      }
    });

    ctx.patchState({ availableScGrades: updatedAvailableScGrades });

    ctx.dispatch(new OnSetSharedGridRowData({
      payload: [formData],
      gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
    }));
    ctx.dispatch(new ResetSelectedRequiredScStock());
  }

  async editNodeMode(ctx: StateContext<SalesShippingInstructionsStateModel>, formData: any) {
    // Get the original data for comparison
    const state = ctx.getState();
    const selectedRowNode = state.selectedRowNodeForEdit;

    if (!selectedRowNode) {
      ctx.dispatch(new ShowToastrAlert({
        showToast: true,
        toastrType: 'error',
        messageTitle: 'Error',
        messageBody: 'No row selected for editing',
        position: 'toast-top-center'
      }));
      return;
    }

    // Calculate the difference in cartons between the edit and the original
    const originalCartons = selectedRowNode.noOfCartons;
    const newCartons = formData.noOfCartons;
    const cartonDifference = newCartons - originalCartons;

    // Handle internal grades stock update
    const availableInternalGrades = state.groupedStockAvailable;
    const updatedGroupedStockAvailable = produce(availableInternalGrades, draft => {
      for (const grade of draft) {
        if (formData.internalGradeId === grade._id) {
          // If reducing cartons, potentially un-deplete the grade
          if (cartonDifference < 0) {
            if (grade.depleted && grade.availableCartons === 0) {
              // Remove depleted flag if the grade was previously depleted
              grade.depleted = false;
            }
            // Increase available cartons by the reduction amount
            grade.availableCartons = grade.availableCartons - cartonDifference; // Negative cartonDifference becomes a positive addition
          }
          // If increasing cartons, check if there's enough and potentially deplete
          else if (cartonDifference > 0) {
            // Check if there are enough cartons available
            if (grade.availableCartons < cartonDifference) {
              ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Insufficient Stock',
                messageBody: `Only ${grade.availableCartons} cartons are available`,
                position: 'toast-top-center'
              }));
              return;
            }

            // Update availableCartons and check if depleted
            grade.availableCartons = grade.availableCartons - cartonDifference;
            if (grade.availableCartons === 0) {
              grade.depleted = true;
            }
          }
          // If same number of cartons, no adjustment needed
        }
      }
    });

    ctx.patchState({ groupedStockAvailable: updatedGroupedStockAvailable });

    // Handle sales confirmation grades stock update
    const availableScGrades = state.availableScGrades;

    // Find the original and new SC grade IDs
    const originalScGradeId = selectedRowNode.salesConfirmationComponentId;
    const newScGradeId = formData.salesConfirmationComponentId;

    // Check if the SC grade has changed or only the quantity
    if (originalScGradeId !== newScGradeId) {
      // Handle SC grade change - need to update both old and new grades
      const updatedAvailableScGrades = produce(availableScGrades, draft => {
        // Return cartons to the original grade
        for (const grade of draft) {
          if (grade.salesConfirmationComponentId === originalScGradeId) {
            grade.remainingCartons += originalCartons;
            // Remove depleted flag if it was depleted
            if (grade.depleted && grade.remainingCartons > 0) {
              grade.depleted = false;
            }
          }
        }

        // Take cartons from the new grade
        for (const grade of draft) {
          if (grade.salesConfirmationComponentId === newScGradeId) {
            // Check if enough remaining stock
            if (grade.remainingCartons < newCartons) {
              ctx.dispatch(new OnShowFuseAlert({
                showAlert: true,
                type: 'error',
                title: 'Insufficient Stock',
                message: `Only ${grade.remainingCartons} cartons are available in this sales confirmation grade.`,
              }));
              throw new Error(`Insufficient stock in sales confirmation grade`);
            }

            grade.remainingCartons -= newCartons;
            if (grade.remainingCartons === 0) {
              grade.depleted = true;
            }
          }
        }
      });

      ctx.patchState({ availableScGrades: updatedAvailableScGrades });
    } else {
      // Same SC grade, just update the quantity
      const updatedAvailableScGrades = produce(availableScGrades, draft => {
        for (const grade of draft) {
          if (grade.salesConfirmationComponentId === originalScGradeId) {
            // Check if enough remaining stock when increasing
            if (cartonDifference > 0 && grade.remainingCartons < cartonDifference) {
              ctx.dispatch(new OnShowFuseAlert({
                showAlert: true,
                type: 'error',
                title: 'Insufficient Stock',
                message: `Only ${grade.remainingCartons} additional cartons are available.`,
              }));
              throw new Error(`Insufficient stock for increase`);
            }

            // Update the remaining cartons
            grade.remainingCartons -= cartonDifference;

            // Update depleted status
            if (grade.remainingCartons === 0) {
              grade.depleted = true;
            } else if (grade.depleted && grade.remainingCartons > 0) {
              grade.depleted = false;
            }
          }
        }
      });

      ctx.patchState({ availableScGrades: updatedAvailableScGrades });
    }

    // Mark the data as edited
    formData.edited = true;

    ctx.dispatch(new OnSetSharedGridRowData({
      reload: true,
      payload: [formData],
      gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
    }));
    // ctx.dispatch(new OnSetSharedGridRowData({
    //   payload: [formData],
    //   update: true,
    //   gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
    // }));

    // Reset form state
    ctx.dispatch(new ResetSelectedRequiredScStock());
    ctx.dispatch(new SetSalesShippingStockEditable(false));
    ctx.dispatch(new SetSalesShippingStockIdentification({ _id: null, md5Checksum: null }));
  }


  @Action(RemoveSalesShippingProduct)
  removeSalesShippingProduct(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: RemoveSalesShippingProduct) {

    const editedRowIndex = ctx.getState().editedRowIndex;

    if (typeof editedRowIndex === 'number') {
      ctx.dispatch(new ShowToastrAlert({
        showToast: true,
        toastrType: 'error',
        messageTitle: 'Error',
        messageBody: 'You cannot delete a sales confirmation component when editing a shipping component.',
        position: 'toast-top-center'
      }));
      return;
    }

    const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
    const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
    const rowData = gridConfig[gridUUIDs.thirdGridUUID].rowData;

    // First update the availableScGrades - restore the cartons that are being removed
    const availableScGrades = ctx.getState().availableScGrades;

    // Find the corresponding sales confirmation component
    const salesConfirmationComponentId = payload.salesConfirmationComponentId;
    const noOfCartons = payload.noOfCartons;

    // Update availableScGrades to restore cartons being removed
    const updatedAvailableScGrades = produce(availableScGrades, draft => {
      for (const grade of draft) {
        if (grade.salesConfirmationComponentId === salesConfirmationComponentId) {
          // Add the cartons back to the available count
          grade.remainingCartons += noOfCartons;
          // If this grade was depleted, un-deplete it since we're adding cartons back
          if (grade.depleted && grade.remainingCartons > 0) {
            grade.depleted = false;
          }
        }
      }
    });

    // Update groupedStockAvailable to restore cartons being removed
    const availableInternalGrades = ctx.getState().groupedStockAvailable;
    const updatedGroupedStockAvailable = produce(availableInternalGrades, draft => {
      for (const grade of draft) {
        // Match by internalGradeId
        if (grade._id === payload.internalGradeId) {
          // Add the cartons back to the available count
          grade.availableCartons += payload.noOfCartons;

          // If this grade was depleted, un-deplete it
          if (grade.depleted && grade.availableCartons > 0) {
            grade.depleted = false;
          }
        }
      }
    });

    // Update the row data
    const updatedRowData = produce(rowData, draft => {
      if (payload._id) {
        for (const shippingRow of draft) {
          if (shippingRow._id === payload._id) {
            if (!shippingRow.edited || ctx.getState().isCloneMode) {
              draft.splice(draft.indexOf(shippingRow), 1);
            } else {
              ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You cannot delete a shipping component that has been edited.',
                position: 'toast-top-center'
              }));
              throw new Error('You cannot delete a shipping component that has been edited.');
            }
          }
        }
      } else {
        const index = draft.findIndex(shippingRow => {
          return (shippingRow.internalGrade === payload.internalGrade) &&
            (shippingRow.externalGrade === payload.externalGrade) &&
            (shippingRow.cropYear === payload.cropYear) &&
            (shippingRow.noOfCartons === payload.noOfCartons) &&
            (shippingRow.cartonNettWeight === payload.cartonNettWeight) &&
            (shippingRow.quantityKgs === payload.quantityKgs);
        });
        if (index !== -1) {
          draft.splice(index, 1);
        }
      }
    });
    // Update state with both restored grade availability
    ctx.patchState({
      availableScGrades: updatedAvailableScGrades,
      groupedStockAvailable: updatedGroupedStockAvailable
    });

    // Handle persisted records with a confirmation dialog
    if (payload._id && !ctx.getState().isCloneMode) {
      const _fuseConfirmationService = inject(FuseConfirmationService);
      const dialogRef = _fuseConfirmationService.open({
        title: 'Delete Shipping Component',
        message: `Are you sure you want to delete this shipping component? <br>
            <strong>This action cannot be undone.</strong>`,
        icon: {
          show: true,
          color: 'primary'
        },
        actions: {
          confirm: {
            color: 'accent'
          },
        },
        dismissible: false
      });

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === 'confirmed') {
          await firstValueFrom(ctx.dispatch(new DeleteSalesShippingInstructionComponent({
            _id: payload._id,
            updatedRowData
          })));
        } else {
          // If canceled, revert the availability changes
          ctx.patchState({
            availableScGrades: availableScGrades,
            groupedStockAvailable: availableInternalGrades
          });
        }
      });
    } else {
      // For non-persisted records, just update the grid
      ctx.dispatch(new OnSetSharedGridRowData({
        reload: true,
        payload: updatedRowData,
        gridUUID: gridUUIDs.thirdGridUUID
      }));
    }
  }

  @Action(DeleteSalesShippingInstructionComponent)
  deleteSalesShippingInstructionComponent(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: DeleteSalesShippingInstructionComponent) {
    return this._salesShippingInstructionsService.deleteSalesShippingInstructionComponent(payload._id).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: payload.updatedRowData,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
        }));
      })
    );
  }

  @Action(GetSalesShippingInstructionsDocumentTemplate)
  getSalesShippingInstructionsDocumentTemplate(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    if (this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fourthGridUUID].rowData.length <= 0) {

      return this._salesShippingInstructionsService.getSalesShippingInstructionsDocumentTemplate({
        type: 'salesDocumentation',
        key: 'salesDocumentation',
        stakeholders: [ctx.getState().salesShippingInstructionsForm.model.customerId]
      }).pipe(
        catchError((error) => {
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: error.error.message.message,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          throw error;
        }),
        tap((data: any) => {
          // const selectedSalesShippingInstruction = ctx.getState().selectedSalesShippingInstruction;
          const processedData = [...data];

          const existingDocuments = ctx.getState().editingSSIDocuments;
          processedData.forEach(item => {
            item.selected = false;
          });
          if (existingDocuments.documentRows && existingDocuments.documentRows.length > 0) {
            for (const item of processedData) {
              const matchingRow = existingDocuments.documentRows.find(row => row._id === item._id);
              if (matchingRow) {
                if (matchingRow.value !== item.value || matchingRow.action !== item.action) {
                  item.value = matchingRow.value;
                  item.action = matchingRow.action;
                }
                item.selected = true;
              }
            }
          }
          ctx.dispatch(new UpdateFormValue({
            path: 'salesShippingInstructions.salesShippingDocumentationNotesForm',
            value: { documentationNotes: data[0].valueFooter || '' }
          }));
          if (ctx.getState().selectedSalesShippingInstruction._id || ctx.getState().isCloneMode) {
            ctx.dispatch(new UpdateFormValue({
              path: 'salesShippingInstructions.salesShippingDocumentationNotesForm',
              value: { documentationNotes: existingDocuments.documentDescription || '' }
            }));
          }
          ctx.dispatch(new OnSetSharedGridRowData({
            reload: true,
            payload: processedData,
            gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fourthGridUUID
          }));
          ctx.dispatch(new SetShowLoader(false));
        })
      );
    } else {
      const gridRowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[
        this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fourthGridUUID
      ].rowData;

      let selectedGridRows = this._store.selectSnapshot(SharedState.getSelectedGridRows).salesDocumentation;

      // Fallback to previously selected rows from the grid data
      if (!selectedGridRows || selectedGridRows.length === 0) {
        selectedGridRows = gridRowData.filter(row => row.selected);
      }

      const selectedIds = new Set(selectedGridRows.map(row => row._id));

      const updatedGridRowData = produce(gridRowData, draft => {
        for (const row of draft) {
          row.selected = selectedIds.has(row._id);
        }
      });

      ctx.dispatch(
        new OnSetSharedGridRowData({
          reload: true,
          payload: updatedGridRowData,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fourthGridUUID,
        })
      );

      ctx.dispatch(new SetShowLoader(false));

    }
  }

  @Action(GetSalesShippingInstructionsSpecialInstructions)
  getSalesShippingInstructionsSpecialInstructions(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    if (this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fifthGridUUID].rowData.length <= 0) {
      return this._salesShippingInstructionsService.getSalesShippingInstructionsDocumentTemplate({
        type: 'specialInstructions',
        key: 'specialInstructions',
        stakeholders: [ctx.getState().salesShippingInstructionsForm.model.customerId]
      }).pipe(
        catchError((error) => {
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: error.error.message.message,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          throw error;
        }),
        tap((data: any) => {
          const processedData = [...data];

          const existingSpecialInstructions = ctx.getState().editingSpecialInstructions;
          processedData.forEach(item => {
            item.selected = false;
          });
          if (existingSpecialInstructions.length > 0) {
            for (const item of processedData) {
              const matchingRow = existingSpecialInstructions.find(row => row._id === item._id);
              if (matchingRow) {
                if (matchingRow.value !== item.value || matchingRow.action !== item.action) {
                  item.value = matchingRow.value;
                  item.action = matchingRow.action;
                }
                item.selected = true;
              }
            }
          }
          ctx.dispatch(new OnSetSharedGridRowData({
            reload: true,
            payload: processedData,
            gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fifthGridUUID
          }));
          ctx.dispatch(new SetShowLoader(false));
        })
      );
    } else {

      const gridRowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[
        this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fifthGridUUID
      ].rowData;

      let selectedGridRows = this._store.selectSnapshot(SharedState.getSelectedGridRows).specialInstructions;

      // Fallback to previously selected rows from the grid data
      if (!selectedGridRows || selectedGridRows.length === 0) {
        selectedGridRows = gridRowData.filter(row => row.selected);
      }

      const selectedIds = new Set(selectedGridRows.map(row => row._id));

      const updatedGridRowData = produce(gridRowData, draft => {
        for (const row of draft) {
          row.selected = selectedIds.has(row._id);
        }
      });

      ctx.dispatch(
        new OnSetSharedGridRowData({
          reload: true,
          payload: updatedGridRowData,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).fifthGridUUID,
        })
      );

      ctx.dispatch(new SetShowLoader(false));
    }
  }

  @Action(SetSelectedRequiredScStock)
  setSelectedRequiredScStock(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSelectedRequiredScStock) {
    ctx.patchState({ selectedRequiredScStock: payload });
  }

  @Selector()
  static getSelectedRequiredScStock(state: SalesShippingInstructionsStateModel) {
    return state.selectedRequiredScStock;
  }

  @Action(SetSelectedPsiGradeStock)
  setSelectedPsiGradeStock(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSelectedPsiGradeStock) {
    ctx.patchState({ selectedPsiGradeStock: payload });
  }

  @Selector()
  static getSelectedPsiGradeStock(state: SalesShippingInstructionsStateModel) {
    return state.selectedPsiGradeStock;
  }

  @Action(ResetSelectedRequiredScStock)
  resetSelectedRequiredScStock(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.patchState({
      selectedRequiredScStock: {
        salesConfirmationComponentId: null,
        internalGrade: null,
        externalGrade: null,
        quantityKgs: null,
        cartonNettWeight: null,
        requiredCartons: null,
        remainingCartons: null
      }
    });
    ctx.dispatch(new UpdateFormValue({
      path: 'salesShippingInstructions.salesShippingStockForm',
      value: {
        parentId: null,
        shipmentReference: null,
        purchaseConfirmationId: null,
        scInternalGrade: null,
        stockInternalGrade: null,
        shippingMark: null,
        cropYear: null,
        storageFacility: null,
        numbering: null,
        quantityKgs: null,
        cartonNettWeight: null,
        noOfCartons: null,
      }
    }));
  }

  @Action(SetSelectedRowNodeForEdit)
  setSelectedRowNodeForEdit(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSelectedRowNodeForEdit) {

    let editedRowIndex = null;
    if (payload) {
      const rowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID].rowData;
      editedRowIndex = rowData.findIndex(row => row.stockInternalGrade === payload.stockInternalGrade && row.scInternalGrade === payload.scInternalGrade && row.shippingMark === payload.shippingMark);
    }
    ctx.patchState({ selectedRowNodeForEdit: payload, editedRowIndex });
  }

  @Selector()
  static getSelectedRowNodeForEdit(state: SalesShippingInstructionsStateModel) {
    return state.selectedRowNodeForEdit;
  }

  @Action(SetSalesShippingStockIdentification)
  setSalesShippingStockIdentification(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSalesShippingStockIdentification) {
    ctx.patchState({ salesShippingStockIdentification: payload });
  }

  @Action(SetSalesShippingStockEditable)
  setSalesShippingStockEditable(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSalesShippingStockEditable) {
    ctx.dispatch(new SetCanRowDrag(payload));
    ctx.patchState({ isSalesShippingStockEditable: payload });
  }

  @Selector()
  static getIsSalesShippingStockEditable(state: SalesShippingInstructionsStateModel) {
    return state.isSalesShippingStockEditable;
  }

  @Action(OnActionSalesShippingInstruction)
  onActionSalesShippingInstruction(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));

    const state = ctx.getState();

    const salesShippingInstructionsForm = state.salesShippingInstructionsForm.model;
    const salesShippingInstructions = state.salesShippingInstructions.model;
    salesShippingInstructions.incotermType = salesShippingInstructions.incotermType.toUpperCase();
    const salesShippingDocumentationNotesForm = state.salesShippingDocumentationNotesForm.model;

    const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
    const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
    const rowData = gridConfig[gridUUIDs.thirdGridUUID].rowData;

    const selectedSalesShippingInstruction = state.selectedSalesShippingInstruction;

    if (rowData.length <= 0) {
      ctx.dispatch(new ShowToastrAlert({
        showToast: true,
        toastrType: 'error',
        messageTitle: 'Error',
        messageBody: 'Please add at least one shipping component',
        position: 'toast-top-center'
      }));
      ctx.dispatch(new SetShowLoader(false));
      return;
    }
    const allDocumentationRows = [...gridConfig[gridUUIDs.fourthGridUUID].rowData, ...gridConfig[gridUUIDs.fifthGridUUID].rowData];
    const allSelectedDocumentationRows = allDocumentationRows.filter(row => row.selected);

    const documentationByType = {};
    allSelectedDocumentationRows.forEach(row => {
      const { _id, type, value, action } = row;
      if (!documentationByType[type]) {
        documentationByType[type] = [];
      }
      const isDuplicate = documentationByType[type].some(doc => doc._id === _id);
      if (!isDuplicate) {
        documentationByType[type].push({
          value,
          action,
          _id
        });
      }
    });

    const apiPayload = {
      salesShippingInstruction: {
        ssiDate: salesShippingInstructionsForm.ssiDate,
        // salesConfirmationId: ctx.getState().isCloneMode ? state.selectedSalesConfirmation._id : salesShippingInstructionsForm.salesConfirmationReferences._id,
        // salesConfirmationNumber: ctx.getState().isCloneMode ? state.selectedSalesConfirmation.salesConfirmationNumber : salesShippingInstructionsForm.salesConfirmationReferences.salesConfirmationNumber,
        salesConfirmationId: salesShippingInstructionsForm.salesConfirmationReferences._id,
        salesConfirmationNumber: salesShippingInstructionsForm.salesConfirmationReferences.salesConfirmationNumber,
        customerReference: salesShippingInstructionsForm.customerReference,
        shipmentTo: salesShippingInstructionsForm.shipmentTo,
        shipmentCc: salesShippingInstructionsForm.shipmentCc,
        customerName: salesShippingInstructionsForm.customerName,
        customerId: salesShippingInstructionsForm.customerId,
        subject: salesShippingInstructionsForm.subject,
        supplierShipperDetails: salesShippingInstructionsForm.supplierShipperDetails,
        consigneeDetails: salesShippingInstructionsForm.consigneeDetails,
        notifyPartyDetails: salesShippingInstructionsForm.notifyPartyDetails,
        additionalNotifyPartyDetails: salesShippingInstructionsForm.additionalNotifyPartyDetails,

        instructions: salesShippingInstructions,
        documentation: {
          documentRows: documentationByType['salesDocumentation'] ? documentationByType['salesDocumentation'] : state.editingSSIDocuments.documentRows,
          documentDescription: salesShippingDocumentationNotesForm.documentationNotes,
        },
        specialInstructions: documentationByType['specialInstructions'] ? documentationByType['specialInstructions'] : state.editingSpecialInstructions,
      },
      confirmationComponents: rowData.map(item => ({
        ...item,
        stockInternalGrade: item.stockInternalGrade,
      })),
    };

    if (selectedSalesShippingInstruction._id && selectedSalesShippingInstruction.md5Checksum) {
      return this._salesShippingInstructionsService.updateSalesShippingInstruction(selectedSalesShippingInstruction._id, selectedSalesShippingInstruction.md5Checksum, apiPayload).pipe(
        catchError((error) => {
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: error.error.message.message,
            position: 'toast-top-center'
          }));
          throw error;
        }),
        tap(() => {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: 'Sales Shipping Instruction updated successfully',
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          ctx.dispatch(new OnCanCloseDialog(true));
        })
      );
    }

    if (ctx.getState().isCloneMode) {
      for (const component of apiPayload.confirmationComponents) {
        delete component._id;
      }
    }

    apiPayload.salesShippingInstruction['status'] = 'PENDING';
    return this._salesShippingInstructionsService.createSalesShippingInstruction(apiPayload).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Sales Shipping Instruction created successfully',
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnCanCloseDialog(true));
      })
    );
  }

  @Action(OnSetCloneMode)
  onSetCloneMode(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: OnSetCloneMode) {
    ctx.patchState({ isCloneMode: payload });
  }

  @Selector()
  static getIsCloneMode(state: SalesShippingInstructionsStateModel) {
    return state.isCloneMode;
  }

  //** Sales Shipping Instructions Status Functions */

  @Action(SetSelectedSalesShippingInstruction)
  setSelectedSalesShippingInstruction(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSelectedSalesShippingInstruction) {
    ctx.patchState({ selectedSalesShippingInstruction: payload });
  }

  @Selector()
  static getSelectedSalesShippingInstruction(state: SalesShippingInstructionsStateModel) {
    return state.selectedSalesShippingInstruction;
  }

  @Action(GetSalesShippingInstructionsStatuses)
  getSalesShippingInstructionsStatuses(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    return this._salesShippingInstructionsService.getDropdownsFilter({ types: ['salesShippingInstructionStatuses'] }).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: { salesShippingInstructionStatuses: string[]; }) => {
        ctx.dispatch(new OnSetSharedDropdowns(response.salesShippingInstructionStatuses));
      })
    );
  }

  @Action(OnSetSalesShippingInstructionStatus)
  onSetSalesShippingInstructionStatus(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    const dropdownStatus = this._store.selectSnapshot(SharedState.getStatusForm).model.status;
    const statusChangeDate = this._store.selectSnapshot(SharedState.getStatusForm).model.statusChangeDate;
    const selectedSalesShippingInstruction = ctx.getState().selectedSalesShippingInstruction;
    ctx.dispatch(new SetShowLoader(true));
    return this._salesShippingInstructionsService.updateSalesShippingInstructionStatus({
      _id: selectedSalesShippingInstruction._id,
      status: dropdownStatus,
      statusChangeDate: statusChangeDate,
    }).pipe(
      //TODO: Add an interface for the response that is generic for this return as its used all over
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: []) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnSetSharedGridRowData({
          update: true,
          payload: response,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
        }));
        ctx.dispatch(new SetSelectedSalesShippingInstruction({ _id: null, md5Checksum: null }));
        ctx.dispatch(new ResetStatusForm());
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Sales Shipping Instruction status updated successfully',
          position: 'toast-top-center'
        }));

      })
    );
  }

  //** Edit */

  /**
   * Caps cloned components to the floor of SC remainingCartons and PSI availableCartons,
   * deducts those pools, and skips rows that cannot take any stock.
   */
  private applyCloneStockAdjustments(
    components: any[],
    availableScGrades: AvailableScGrade[],
    groupedStockAvailable: StockAvailableGrade[]
  ): {
    components: any[];
    availableScGrades: AvailableScGrade[];
    groupedStockAvailable: StockAvailableGrade[];
    skippedCount: number;
    cappedCount: number;
  } {
    let skippedCount = 0;
    let cappedCount = 0;
    const adjustedComponents: any[] = [];
    const scDraft = availableScGrades.map((grade) => ({ ...grade }));
    const psiDraft = groupedStockAvailable.map((grade) => ({ ...grade }));

    for (const component of components) {
      const scGrade = scDraft.find(
        (grade) => grade.salesConfirmationComponentId === component.salesConfirmationComponentId
      );
      const psiGrade = psiDraft.find((grade) => grade._id === component.internalGradeId);

      const requestedCartons = Number(component.noOfCartons) || 0;
      const scRemaining = scGrade?.remainingCartons ?? 0;
      const psiAvailable = psiGrade?.availableCartons ?? 0;
      const cappedCartons = Math.min(requestedCartons, scRemaining, psiAvailable);

      if (cappedCartons <= 0) {
        skippedCount += 1;
        continue;
      }

      if (cappedCartons < requestedCartons) {
        cappedCount += 1;
      }

      const cartonNettWeight = Number(component.cartonNettWeight) || 0;
      adjustedComponents.push({
        ...component,
        isCloned: true,
        noOfCartons: cappedCartons,
        quantityKgs: cappedCartons * cartonNettWeight,
      });

      if (scGrade) {
        scGrade.remainingCartons -= cappedCartons;
        scGrade.depleted = scGrade.remainingCartons <= 0;
      }

      if (psiGrade) {
        psiGrade.availableCartons -= cappedCartons;
        psiGrade.quantityKgs = psiGrade.availableCartons * (psiGrade.cartonNettWeight || 0);
        psiGrade.depleted = psiGrade.availableCartons <= 0;
      }
    }

    return {
      components: adjustedComponents,
      availableScGrades: scDraft,
      groupedStockAvailable: psiDraft,
      skippedCount,
      cappedCount,
    };
  }

  @Action(OnGetDataForEditOrClone)
  async onGetDataForEditOrClone(ctx: StateContext<SalesShippingInstructionsStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    const selectedSalesShippingInstruction = ctx.getState().selectedSalesShippingInstruction;
    await firstValueFrom(this._store.dispatch(new GetAvailableSalesConfirmations()));

    if (ctx.getState().isCloneMode) {
      await firstValueFrom(this._store.dispatch(new GetStockAvailable()));
    }

    return this._salesShippingInstructionsService.getSalesShippingInstructionComponentsById(selectedSalesShippingInstruction._id, selectedSalesShippingInstruction.md5Checksum, false).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((response: any) => {
        const rowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID].rowData;

        const foundRow = rowData.find((row: any) => row._id === selectedSalesShippingInstruction._id);

        if (ctx.getState().isCloneMode) {
          this._store.dispatch(new SetSelectedSalesShippingInstruction({ _id: null, md5Checksum: null }));

          const cloneAdjustments = this.applyCloneStockAdjustments(
            response,
            ctx.getState().availableScGrades,
            ctx.getState().groupedStockAvailable
          );

          ctx.patchState({
            availableScGrades: cloneAdjustments.availableScGrades,
            groupedStockAvailable: cloneAdjustments.groupedStockAvailable,
          });

          response = cloneAdjustments.components;

          if (cloneAdjustments.skippedCount > 0 || cloneAdjustments.cappedCount > 0) {
            ctx.dispatch(new ShowToastrAlert({
              showToast: true,
              toastrType: 'warning',
              messageTitle: 'Clone stock adjusted',
              messageBody: `${cloneAdjustments.cappedCount} component(s) capped to available stock and ${cloneAdjustments.skippedCount} skipped with no remaining stock.`,
              position: 'toast-top-center'
            }));
          }
        }

        ctx.patchState({
          editingSSIDocuments: foundRow.documentation,
          editingSpecialInstructions: foundRow.specialInstructions
        });
        ctx.dispatch(new UpdateFormValue({
          path: 'salesShippingInstructions.salesShippingInstructionsForm',
          value: {
            ssiDate: foundRow.ssiDate,
            salesConfirmationReferences: {
              _id: foundRow.salesConfirmationId,
              salesConfirmationNumber: foundRow.salesConfirmationNumber,
            },
            status: foundRow.status,
            customerReference: foundRow.customerReference,
            shipmentTo: foundRow.shipmentTo,
            shipmentCc: foundRow.shipmentCc,
            customerName: foundRow.customerName,
            customerId: foundRow.customerId,
            subject: foundRow.subject,
            supplierShipperDetails: foundRow.supplierShipperDetails,
            consigneeDetails: foundRow.consigneeDetails,
            notifyPartyDetails: foundRow.notifyPartyDetails,
            additionalNotifyPartyDetails: foundRow.additionalNotifyPartyDetails,
          }
        }));

        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: response,
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID
        }));

        ctx.dispatch(new UpdateFormValue({
          path: 'salesShippingInstructions.salesShippingInstructions',
          value: foundRow.instructions
        }));

        ctx.dispatch(new SetShowLoader(false));
      })
    );
  }

  //?? OnActionSalesShippingInstruction could be used?
  @Action(OnSetSalesShippingInstructionDates)
  onSetSalesShippingInstructionDates(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: OnSetSalesShippingInstructionDates) {
    ctx.dispatch(new SetShowLoader(true));
    const datesForm = ctx.getState().datesForm.model;

    return this._salesShippingInstructionsService.updateSalesShippingInstructionDates({
      _id: payload._id,
      md5Checksum: payload.md5Checksum,
      dates: datesForm,
    }).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        const errPayload = error?.error;
        const nested = errPayload?.message;
        const messageBody =
          typeof nested === 'string'
            ? nested
            : typeof nested?.message === 'string'
              ? nested.message
              : typeof error?.message === 'string'
                ? error.message
                : 'Failed to update sales shipping instruction dates';
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Sales Shipping Instruction dates updated successfully',
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new OnCanCloseDialog(true));
      })
    );
  }

  //** Delete */

  @Action(OnDeleteSalesShippingInstruction)
  onDeleteSalesShippingInstruction(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: OnDeleteSalesShippingInstruction) {
    ctx.dispatch(new SetShowLoader(true));
    return this._salesShippingInstructionsService.deleteSalesShippingInstruction(payload).pipe(
      catchError((error) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: error.error.message.message,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: 'Sales Shipping Instruction deleted successfully',
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new GetSalesShippingInstructions());
      })
    );
  }

  //** Cell Renderer */
  @Action(SetSalesShippingComponentsGridConfig)
  setSalesShippingComponentsGridConfig(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: SetSalesShippingComponentsGridConfig) {

    const store = inject(Store);
    const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

    const updatedGridConfig = produce(parentGridConfig, (draft) => {

      draft[payload.gridUUID] = payload.gridConfig[payload.gridUUID];
    });
    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));

  }

  @Action(OnSetCurrentSSIRenderer)
  onSetCurrentRenderer(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: OnSetCurrentSSIRenderer) {
    ctx.patchState({
      currentRendererId: payload.currentRenderer
    });
  }


  @Selector()
  static getCurrentRendererId(state: SalesShippingInstructionsStateModel) {
    return state.currentRendererId;
  }

  @Action(GetSalesShippingComponentsByParentId)
  getSalesShippingComponentsByParentId(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: GetSalesShippingComponentsByParentId) {
    return this._salesShippingInstructionsService.getSalesShippingComponentsByParentId(payload.parentId).pipe(
      tap((response: any) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: response,
          gridUUID: payload.gridUUID
        }));
      })
    );
  }

  //** webhooks */

  @Action(CreatedSalesShippingInstructionWebhook)
  createdSalesShippingInstructionWebhook(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: CreatedSalesShippingInstructionWebhook) {
    if (payload) {
      ctx.dispatch(new GetSalesShippingInstructions());
    }
  }

  @Action(UpdatedSalesShippingInstructionWebhook)
  updatedSalesShippingInstructionWebhook(ctx: StateContext<SalesShippingInstructionsStateModel>, { payload }: UpdatedSalesShippingInstructionWebhook) {
    const isOnSSIScreen = this._router.url.includes('sales-shipping-instructions');
    if (payload && isOnSSIScreen) {
      ctx.dispatch(new GetSalesShippingInstructions());
    }
  }
}
